/* Saéa Refine tray. Progressive enhancement, partial: every filter link is a
   real href and the sort control has a <noscript> submit fallback, so those
   work with JS disabled. The tray itself — open/close, focus trap — is
   JS-only: with JS disabled there is no way to open it, so filtering is
   unreachable through it. */

/* Scroll the control row (.saea-controls — Refine button, applied chips,
   count, sort) into view after a filter or sort reload. This collection page
   stacks ~8 sections (announcement bar, header, hero, sub-nav, UGC carousel,
   editorial grid) above the grid, so a filtered reload otherwise lands the
   shopper back at the very top, hiding the change they just made.
   Deliberately standalone — not part of the <saea-facets-tray> lifecycle
   below, and touches none of its open/close/focus-trap/inert logic. Runs
   first (this script has no other top-level code above it), before
   customElements.define() below synchronously upgrades the already-parsed
   <saea-facets-tray>. The tray itself stays CLOSED after an Apply navigation
   (see initBatching()'s Apply handler and connectedCallback() below — there
   is no auto-reopen), so this scroll is what actually puts the confirmation
   of what changed — the count and applied chips — in front of the shopper.

   Targets .saea-controls, NOT the grid: .saea-controls carries
   `position:sticky; top:0` (see saea-facets-tray.css), but its containing
   block (<saea-facets-tray>) is exactly as tall as the row itself, so it has
   zero scroll travel and unsticks the instant it reaches the top — it does
   NOT follow the shopper down the page (that would be a design change
   nobody asked for). Scrolling the GRID into view with `block:'start'`
   therefore pushed the entire control row — Refine button, applied chips,
   count, sort — above the viewport, landing the shopper on product
   photography with no visible sign anything happened. Scrolling to
   .saea-controls instead, with a small offset so it isn't flush against the
   browser chrome, keeps the row on screen with the first row of products
   directly beneath it. Falls back to the grid if .saea-controls is ever
   missing (defensive; it always renders as part of this snippet).

   Instant scroll only — the page has already navigated, so a smooth
   animated scroll here would just be disorienting; this also makes
   prefers-reduced-motion moot, since no motion is introduced. */
(function scrollToResultsIfFilteredOrSorted() {
  var params = new URLSearchParams(location.search);
  var isFilteredOrSorted = false;
  for (var key of params.keys()) {
    if (key === 'sort_by' || key.indexOf('filter.') === 0) {
      isFilteredOrSorted = true;
      break;
    }
  }
  if (!isFilteredOrSorted) return;
  var OFFSET = 14; // breathing room above the control row, px
  var controls = document.querySelector('.saea-controls');
  if (controls) {
    var top = controls.getBoundingClientRect().top + window.scrollY - OFFSET;
    window.scrollTo(0, Math.max(top, 0));
    return;
  }
  var grid = document.getElementById('ProductGridContainer') || document.getElementById('product-grid');
  if (!grid) return;
  grid.scrollIntoView({ block: 'start', behavior: 'auto' });
})();

class SaeaFacetsTray extends HTMLElement {
  connectedCallback() {
    this.tray = this.querySelector('#SaeaTray');
    this.scrim = this.querySelector('#SaeaTrayScrim');
    if (!this.tray) return;

    // Dialog semantics belong here, not in the static Liquid markup — see the
    // comment on #SaeaTray in the snippet. Only a successful upgrade (this
    // line running at all) means the tray is really an openable/closable
    // modal; the no-JS and failed-upgrade fallbacks never reach this, so the
    // flattened panel is correctly announced as a plain region instead.
    this.tray.setAttribute('role', 'dialog');
    this.tray.setAttribute('aria-modal', 'true');

    // Toggle, not just open: the button is the primary control on the row
    // and must close the tray on a second click. open() itself still no-ops
    // when already open (see its comment) — that guard is defensive (a
    // double-fire on this listener), not load-bearing for this toggle.
    this.querySelectorAll('[data-saea-tray-open]').forEach((b) =>
      b.addEventListener('click', () => {
        if (this.tray && this.tray.classList.contains('is-open')) this.close();
        else this.open();
      }));
    this.querySelectorAll('[data-saea-tray-close]').forEach((b) =>
      b.addEventListener('click', () => this.close()));

    this.querySelectorAll('.saea-grp__t').forEach((b) =>
      b.addEventListener('click', () => {
        const grp = b.closest('.saea-grp');
        grp.classList.toggle('is-shut');
        b.setAttribute('aria-expanded', String(!grp.classList.contains('is-shut')));
      }));

    // Stone "show more" truncation. Liquid already rendered every value —
    // .saea-opt--extra only exists on non-active values past the threshold
    // (see the snippet) — so this just decides whether to hide that tail.
    // Skipping when there's nothing to hide keeps a same-labelled toggle
    // from ever appearing inert (e.g. if every extra happens to be active).
    this.querySelectorAll('[data-saea-truncate-list]').forEach((list) => {
      const toggle = this.querySelector(`[data-saea-truncate-toggle][aria-controls="${list.id}"]`);
      if (!toggle || !list.querySelector('.saea-opt--extra')) return;
      list.classList.add('is-collapsed');
      toggle.hidden = false;
      toggle.textContent = toggle.dataset.labelMore;
      toggle.addEventListener('click', () => {
        const collapsed = list.classList.toggle('is-collapsed');
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.textContent = collapsed ? toggle.dataset.labelMore : toggle.dataset.labelFewer;
      });
    });

    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trap(e);
    };

    // Batched Apply (Changes 1, 2, 5). An Apply navigation lands with the
    // tray CLOSED — there is no auto-reopen. Under the old instant-apply
    // model, every option click navigated immediately, so reopening the
    // tray after each reload was what kept filtering feel continuous; now
    // nothing navigates until the shopper presses Apply, which means they
    // are DONE choosing, and the correct result is the filtered grid with
    // the tray out of the way (see the scroll-to-results IIFE above).
    this.initBatching();

    // A native closed <select> fires `change` on EVERY arrow-key press in most
    // desktop browsers, not only when the user commits a choice — naive
    // change -> submit therefore navigates the page at each intermediate
    // option and a keyboard user can never reach the one they actually want.
    // A pointer (mouse/touch) selection is unambiguous: exactly one
    // pointerdown always precedes the one `change` that commits it, so that
    // path still submits immediately, in a single interaction.
    //
    // A keyboard-driven change instead waits for an explicit commit — Enter,
    // or leaving the field (blur). But whether Enter/blur ever get a chance
    // to fire first is platform-dependent: if the open dropdown is an
    // OS-level popup, arrow keys never reach the page and `viaPointer` stays
    // true throughout (handled above). If arrow keys DO reach the page while
    // the popup is open, a keydown can flip `viaPointer` to false AFTER the
    // pointerdown that opened it, and the Enter that commits the popup's
    // selection fires its keydown before the resulting `change` — so
    // `pendingSubmit` isn't armed yet when that Enter is handled, and the
    // commit is missed. A debounce fallback closes that gap without assuming
    // which platform behavior we're on: every keyboard-driven `change` (re)
    // arms a timer, so a user who stops without an explicit commit still
    // gets there. Enter/blur cancel the timer when they commit first, and
    // the timer itself funnels through the same commit() so nothing can
    // double-submit.
    const sortSel = this.querySelector('#SaeaSortBy');
    if (sortSel) {
      let viaPointer = false;
      let pendingSubmit = false;
      let debounceTimer = null;

      const clearDebounce = () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
      };
      const commit = () => {
        clearDebounce();
        pendingSubmit = false;
        sortSel.form.submit();
      };

      sortSel.addEventListener('pointerdown', () => { viaPointer = true; });
      sortSel.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          if (pendingSubmit) {
            e.preventDefault();
            commit();
          }
          return;
        }
        viaPointer = false;
      });
      sortSel.addEventListener('change', () => {
        if (viaPointer) {
          commit();
        } else {
          pendingSubmit = true;
          clearDebounce();
          debounceTimer = setTimeout(commit, 700);
        }
      });
      sortSel.addEventListener('blur', () => {
        if (pendingSubmit) commit();
      });
    }
  }

  focusables() {
    return [...this.tray.querySelectorAll('a[href], button:not([disabled]), input')]
      .filter((el) => el.offsetParent !== null);
  }

  // The Refine button lives outside #SaeaTray (in .saea-controls), so
  // focusables() — scoped to this.tray — never included it, and the two
  // boundary checks below used to treat the tray's own first/last element as
  // the whole cycle. That let Tab walk off the last tray element and out of
  // the trap entirely (nothing else non-inert follows it in the DOM), which
  // is the "escape hatch" the button must not be. It belongs one step before
  // the tray content in the ring — that is also its true position in the
  // live (non-inert) DOM: with .saea-sort/.saea-count/.saea-chips/.saea-applied
  // individually inerted while open (see hideBackground()), the browser's own
  // Shift+Tab from the tray's first element already lands on the button
  // without any help from this method, which is why only two of the four
  // ring edges need an explicit check here — the same shape as before this
  // button was added, just with the button standing in for "first" on the
  // wrap-around ends.
  //   ring:  Refine button -> tray's first focusable -> ... -> tray's last
  //          focusable -> (wraps) -> Refine button
  trap(e) {
    const f = this.focusables();
    const refineBtn = this.querySelector('[data-saea-tray-open]');
    const hasRefine = !!(refineBtn && refineBtn.offsetParent !== null);
    if (!f.length && !hasRefine) return;
    const ringFirst = hasRefine ? refineBtn : f[0];
    const ringLast = f.length ? f[f.length - 1] : refineBtn;
    if (e.shiftKey && document.activeElement === ringFirst) { e.preventDefault(); ringLast.focus(); }
    else if (!e.shiftKey && document.activeElement === ringLast) { e.preventDefault(); ringFirst.focus(); }
  }

  // ==========================================================================
  // BATCHED APPLY (Changes 1, 2, 3, 5)
  //
  // Every option below is a real <a href="{{ value.url_to_add }}">, untouched
  // — this method never rewrites a single href. It only READS each one, once,
  // to recover the exact Shopify filter param+value pair that link encodes
  // (see paramPairFromDiff below), so the eventual combined Apply URL is
  // built entirely from values Liquid already computed — never hand-built,
  // which matters most for the taxonomy filters (color/metal) whose values
  // are opaque GIDs. Clicking an option now just toggles PENDING state
  // in-memory and recomputes counts; nothing navigates until the footer
  // button is pressed. With JS disabled this method never runs at all, so
  // every href still applies immediately on click — the original, unbatched
  // behaviour — exactly as it did before this feature.
  // ==========================================================================
  initBatching() {
    const dataEl = this.querySelector('#SaeaFacetData');
    const body = this.querySelector('#SaeaTrayBody');
    if (!dataEl || !body) return;

    let blob;
    try {
      blob = JSON.parse(dataEl.textContent);
    } catch (err) {
      return; // malformed/missing blob — options keep their plain href behaviour
    }
    const products = blob.products || [];

    // label -> index, one map per indexed group. Built from the SAME arrays
    // the Liquid snippet renders its options from (stone_colors/metal_values
    // constants, results.filters labels for stone/piece — see the comment on
    // the <script id="SaeaFacetData"> tag), so a label found in the DOM can
    // never fail to resolve here.
    const labelIndex = { color: {}, stone: {}, metal: {}, piece: {} };
    (blob.colors || []).forEach((l, i) => { labelIndex.color[l] = i; });
    (blob.stones || []).forEach((l, i) => { labelIndex.stone[l] = i; });
    (blob.metals || []).forEach((l, i) => { labelIndex.metal[l] = i; });
    (blob.pieces || []).forEach((l, i) => { labelIndex.piece[l] = i; });

    const pending = {
      color: new Set(), stone: new Set(), metal: new Set(), piece: new Set(),
      availability: new Set(), priceMin: null, priceMax: null,
    };

    // Recover the [paramName, paramValue] pair a given option's href adds or
    // removes, by diffing its querystring against the OTHER of "current
    // page URL" / "this option's href" (whichever side doesn't carry the
    // value). Multiplicity-aware (a \0-joined key consumed on match) so a
    // group with several already-active values still isolates exactly the
    // one pair unique to this option, not a false match against an
    // unrelated repeated param.
    const currentHref = location.href;
    function paramPairFromDiff(hrefWithExtra, hrefWithout) {
      let a;
      let b;
      try {
        a = new URLSearchParams(new URL(hrefWithExtra, currentHref).search);
        b = new URLSearchParams(new URL(hrefWithout, currentHref).search);
      } catch (err) {
        return null;
      }
      const bKeys = [...b.entries()].map(([k, v]) => `${k} ${v}`);
      for (const [k, v] of a.entries()) {
        const key = `${k} ${v}`;
        const idx = bKeys.indexOf(key);
        if (idx === -1) return [k, v];
        bKeys.splice(idx, 1);
      }
      return null;
    }

    // idxListByEl — every option, single-value or consolidated, resolves to
    // an ARRAY of one-or-more indices within its group. A plain color/stone/
    // piece option always resolves to exactly one; a consolidated metal
    // swatch (data-values, a comma list of underlying labels — see the
    // snippet) resolves to several. Every consumer below (click toggle,
    // recompute's count/pending checks) then treats both shapes identically
    // via .some()/.forEach() over the array, with a single-item array
    // behaving exactly like a plain option's did before Change 1.
    const idxListByEl = new Map();

    // metalGidByIndex — index -> raw GID, built from blob.metalGids (label
    // keyed) via labelIndex.metal (label -> index), so a metal swatch's
    // Apply-time contribution can be looked up directly by index without
    // any DOM href to diff against. This is what replaced the earlier
    // hidden-per-value-link approach — a coordinator review caught that
    // approach rendering genuinely dead markup AND, worse, that the visible
    // swatch's own href only ever carried ONE underlying GID while its
    // label advertised the union (see the CHANGE 1 comment on the metal
    // swatch in the snippet, and the matching one on blob.metalGids there).
    const metalGidByIndex = {};
    Object.keys(blob.metalGids || {}).forEach((label) => {
      const idx = labelIndex.metal[label];
      if (idx !== undefined) metalGidByIndex[idx] = blob.metalGids[label];
    });

    const optionEls = [...body.querySelectorAll('.saea-opt, .saea-sw')];
    optionEls.forEach((el) => {
      const group = el.dataset.group;
      const isActive = el.getAttribute('aria-pressed') === 'true';
      // Consolidated metal swatches never capture their own [param, value]
      // pair via DOM diffing — there is no raw per-value link left to diff
      // against (Change 1's coordinator fix removed it; see the snippet).
      // Their Apply-time contribution comes from metalGidByIndex instead
      // (below, in the footer Apply handler), one entry per pending
      // underlying index, which is what actually satisfies "every GID this
      // swatch represents" rather than a single captured pair ever could.
      const isConsolidatedBucket = !!el.dataset.values;
      if (!isConsolidatedBucket) {
        const pair = isActive
          ? paramPairFromDiff(currentHref, el.href)
          : paramPairFromDiff(el.href, currentHref);
        if (pair) {
          el.dataset.saeaParam = pair[0];
          el.dataset.saeaParamValue = pair[1];
        }
      }
      if (group === 'availability') {
        // Availability isn't one indexed list like the other groups — it's a
        // straight boolean on each product record (rec[5]) — but this filter
        // can render MORE than one value ("In stock" AND "Out of stock" both
        // exist on this catalog; an earlier version of this code assumed only
        // "In stock" existed and silently mis-counted "Out of stock" as 0).
        // Rather than guess the mapping from label text, self-calibrate: the
        // server already told us this option's true count before any pending
        // change — whichever boolean (available vs not) produces that exact
        // count from the blob IS what this label means. Falls back to
        // "available" if neither matches (shouldn't happen).
        const serverCount = Number((el.querySelector('.c') || {}).textContent) || 0;
        const countAvailable = products.filter((rec) => rec[5] === 1).length;
        const target = serverCount === countAvailable ? 1 : 0;
        el.dataset.saeaAvailTarget = String(target);
        if (isActive) pending.availability.add(target);
        return;
      }
      const map = labelIndex[group];
      const labels = isConsolidatedBucket
        ? el.dataset.values.split(',')
        : (el.dataset.value !== undefined ? [el.dataset.value] : []);
      const idxList = labels
        .map((l) => (map ? map[l] : undefined))
        .filter((i) => i !== undefined);
      if (!idxList.length) return;
      idxListByEl.set(el, idxList);
      if (isActive) idxList.forEach((i) => pending[group].add(i));
    });

    // Count as if THIS option's own group weren't applied at all (excludeGroup)
    // — the same semantics results.filters.values[].count already uses server-
    // side, so a value's number never moves just because a sibling in the same
    // group is also selected, only when a DIFFERENT group's selection changes.
    function matchesExcluding(rec, excludeGroup) {
      const [c, s, m, t, p, a] = rec;
      if (excludeGroup !== 'color' && pending.color.size && !c.some((i) => pending.color.has(i))) return false;
      if (excludeGroup !== 'stone' && pending.stone.size && !s.some((i) => pending.stone.has(i))) return false;
      if (excludeGroup !== 'metal' && pending.metal.size && !m.some((i) => pending.metal.has(i))) return false;
      if (excludeGroup !== 'piece' && pending.piece.size && !pending.piece.has(t)) return false;
      if (excludeGroup !== 'availability' && pending.availability.size && !pending.availability.has(a)) return false;
      if (excludeGroup !== 'price' && (pending.priceMin !== null || pending.priceMax !== null)) {
        if (pending.priceMin !== null && p < pending.priceMin) return false;
        if (pending.priceMax !== null && p > pending.priceMax) return false;
      }
      return true;
    }
    function hasIndex(rec, group, idx) {
      if (group === 'color') return rec[0].includes(idx);
      if (group === 'stone') return rec[1].includes(idx);
      if (group === 'metal') return rec[2].includes(idx);
      if (group === 'piece') return rec[3] === idx;
      return false;
    }

    const showBtn = this.querySelector('#SaeaTrayShow');

    // Truncation guard. blob.truncated is set server-side (see the comment
    // on the <script id="SaeaFacetData"> tag) when facet_products didn't
    // actually cover every product the current filters match — the 250-item
    // paginate ceiling, or a caller that never passed facet_products at all.
    // A silently-wrong "Blue 35" that turns out to mean something else once
    // applied is worse than no live number at all, so in this state
    // recompute() returns immediately: it does NOT touch any option's count
    // or greying (whatever Liquid rendered server-side stays exactly as
    // rendered — see is_zero in the snippet, still accurate for the CURRENT
    // server-applied filters, just not re-projectable against a NEW pending
    // selection) and the footer never claims a total it cannot compute.
    // Batching, pending toggles and Apply all keep working — only the live
    // projection turns off; the eventual navigation still lands on the
    // server's real count.
    const recompute = () => {
      if (blob.truncated) {
        if (showBtn) {
          showBtn.disabled = false;
          showBtn.textContent = 'Apply filters';
        }
        return null;
      }
      optionEls.forEach((el) => {
        const group = el.dataset.group;
        let count;
        let isPending;
        if (group === 'availability') {
          const target = Number(el.dataset.saeaAvailTarget);
          isPending = pending.availability.has(target);
          count = products.filter((rec) => rec[5] === target && matchesExcluding(rec, 'availability')).length;
        } else {
          const idxList = idxListByEl.get(el);
          if (!idxList || !idxList.length) return;
          isPending = idxList.some((i) => pending[group].has(i));
          // UNION across every index this element represents (idxList.some),
          // never a sum — a consolidated metal swatch's count is the number
          // of DISTINCT products matching ANY of its underlying taxonomy
          // values (Change 1); for a single-index element this is identical
          // to the old single-idx match.
          count = products.filter((rec) => idxList.some((i) => hasIndex(rec, group, i)) && matchesExcluding(rec, group)).length;
        }
        const countEl = el.querySelector('.c');
        if (countEl) countEl.textContent = count;
        const isZero = count === 0 && !isPending;
        el.classList.toggle('is-empty', isZero);
        if (isZero) {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-disabled', 'true');
        } else {
          el.removeAttribute('tabindex');
          el.removeAttribute('aria-disabled');
        }
      });
      const total = products.filter((rec) => matchesExcluding(rec, null)).length;
      if (showBtn) {
        showBtn.disabled = total === 0;
        showBtn.textContent = total === 0 ? 'No pieces match' : `Show ${total} piece${total === 1 ? '' : 's'}`;
      }
      return total;
    };

    optionEls.forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        if (el.getAttribute('aria-disabled') === 'true') return;
        const group = el.dataset.group;
        if (group === 'availability') {
          const target = Number(el.dataset.saeaAvailTarget);
          const nowOn = !pending.availability.has(target);
          if (nowOn) pending.availability.add(target); else pending.availability.delete(target);
          el.setAttribute('aria-pressed', String(nowOn));
        } else {
          const idxList = idxListByEl.get(el);
          if (!idxList || !idxList.length) return;
          // Toggle every index this element represents together (a
          // consolidated metal swatch carries several — Change 1): if ANY
          // is currently pending the whole element reads as active, so a
          // click clears ALL of them; otherwise a click sets ALL of them.
          // Identical to the old single-idx toggle when idxList has one
          // entry.
          const nowOn = !idxList.some((i) => pending[group].has(i));
          idxList.forEach((i) => { if (nowOn) pending[group].add(i); else pending[group].delete(i); });
          el.setAttribute('aria-pressed', String(nowOn));
        }
        recompute();
      });
    });

    // Price — Change 5. Slider + number inputs stay linked both ways; the
    // number inputs are the only thing a no-JS/failed-upgrade visitor ever
    // sees (see the comment on #SaeaPriceSlider in the snippet).
    const minInput = this.querySelector('#SaeaPriceMin');
    const maxInput = this.querySelector('#SaeaPriceMax');
    const slider = this.querySelector('#SaeaPriceSlider');
    const handleMin = this.querySelector('#SaeaPriceHandleMin');
    const handleMax = this.querySelector('#SaeaPriceHandleMax');
    const applyBtn = this.querySelector('.saea-price__apply');
    // Hoisted out of the `if` block below (rather than left as its consts)
    // so Clear All — further down — can reset the slider back to its full
    // range using the exact same fill/label math, instead of duplicating it.
    let rangeMin = 0;
    let rangeMax = 0;
    let syncFill = () => {};
    let setValueText = () => {};
    if (minInput && maxInput && slider && handleMin && handleMax) {
      rangeMin = Number(slider.dataset.min) || 0;
      rangeMax = Number(slider.dataset.max) || 0;
      const currency = blob.currencySymbol || '$';
      slider.hidden = false;
      if (applyBtn) applyBtn.hidden = true;

      const pct = (v) => (rangeMax === rangeMin ? 0 : ((v - rangeMin) / (rangeMax - rangeMin)) * 100);
      syncFill = () => {
        slider.style.setProperty('--saea-price-min-pct', `${pct(Number(handleMin.value))}%`);
        slider.style.setProperty('--saea-price-max-pct', `${pct(Number(handleMax.value))}%`);
      };
      setValueText = (input, v) => input.setAttribute('aria-valuetext', `${currency}${Math.round(Number(v))}`);
      const updatePendingPrice = (minV, maxV) => {
        pending.priceMin = minV <= rangeMin ? null : Math.round(minV * 100);
        pending.priceMax = maxV >= rangeMax ? null : Math.round(maxV * 100);
        recompute();
      };

      handleMin.addEventListener('input', () => {
        // Prevent min exceeding max (both directions): clamp the handle
        // that just moved against the other one's current value.
        if (Number(handleMin.value) > Number(handleMax.value)) handleMin.value = handleMax.value;
        minInput.value = handleMin.value;
        setValueText(handleMin, handleMin.value);
        syncFill();
        updatePendingPrice(Number(handleMin.value), Number(handleMax.value));
      });
      handleMax.addEventListener('input', () => {
        if (Number(handleMax.value) < Number(handleMin.value)) handleMax.value = handleMin.value;
        maxInput.value = handleMax.value;
        setValueText(handleMax, handleMax.value);
        syncFill();
        updatePendingPrice(Number(handleMin.value), Number(handleMax.value));
      });
      // `input` clamps the SLIDER/pending state on every keystroke but
      // deliberately leaves the field's own text alone — rewriting it
      // mid-type (e.g. typing "999" one digit at a time) would fight the
      // cursor. `change` (blur/Enter, i.e. editing is actually done)
      // normalizes the visible number back to whatever it actually
      // resolved to, so a value like 999 typed against a $282 ceiling
      // reads as $282 once the shopper is finished, not silently wrong.
      minInput.addEventListener('input', () => {
        let v = Number(minInput.value);
        if (Number.isNaN(v)) return;
        if (v < rangeMin) v = rangeMin;
        if (v > Number(handleMax.value)) v = Number(handleMax.value);
        handleMin.value = v;
        setValueText(handleMin, v);
        syncFill();
        updatePendingPrice(v, Number(handleMax.value));
      });
      minInput.addEventListener('change', () => { minInput.value = handleMin.value; });
      maxInput.addEventListener('input', () => {
        let v = Number(maxInput.value);
        if (Number.isNaN(v)) return;
        if (v > rangeMax) v = rangeMax;
        if (v < Number(handleMin.value)) v = Number(handleMin.value);
        handleMax.value = v;
        setValueText(handleMax, v);
        syncFill();
        updatePendingPrice(Number(handleMin.value), v);
      });
      maxInput.addEventListener('change', () => { maxInput.value = handleMax.value; });

      setValueText(handleMin, handleMin.value);
      setValueText(handleMax, handleMax.value);
      syncFill();
      // Seed pending price from the server-rendered values (identical on the
      // inputs and the handles at load — see the snippet) so recompute()'s
      // very first run matches results.filters exactly, before any interaction.
      if (minInput.value !== '') pending.priceMin = Math.round(Number(minInput.value) * 100);
      if (maxInput.value !== '') pending.priceMax = Math.round(Number(maxInput.value) * 100);
    }

    // Clear All (Bug 1 fix) — resets every PENDING selection back to
    // nothing: all facet groups, plus price (which shares the same pending
    // state, so the slider/number-fields reset alongside it). Deliberately
    // does NOT touch anything that reflects the page's currently APPLIED
    // filters — the chips + invitation copy in .saea-selected and the
    // Refine button's active count are server-rendered from
    // results.filters and only change once Apply navigates. The element
    // stays a real <a href="{{ results.url }}"> in the Liquid (see the
    // snippet) so a no-JS/failed-upgrade visitor still gets the one thing
    // that page can do there: navigate to the unfiltered collection. JS
    // intercepts with preventDefault(), clears + recomputes in place, and
    // deliberately never navigates — the tray stays open.
    const clearBtn = this.querySelector('#SaeaTrayClear');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        pending.color.clear();
        pending.stone.clear();
        pending.metal.clear();
        pending.piece.clear();
        pending.availability.clear();
        pending.priceMin = null;
        pending.priceMax = null;
        optionEls.forEach((el) => el.setAttribute('aria-pressed', 'false'));
        if (minInput && maxInput && handleMin && handleMax) {
          handleMin.value = String(rangeMin);
          handleMax.value = String(rangeMax);
          // Blank, not the numeric range — matches how these fields render
          // server-side with no filter applied (see #SaeaPriceMin/Max in
          // the snippet), and how initBatching()'s own price-seeding step
          // (above) treats '' as "no constraint" going in.
          minInput.value = '';
          maxInput.value = '';
          setValueText(handleMin, rangeMin);
          setValueText(handleMax, rangeMax);
          syncFill();
        }
        recompute();
      });
    }

    // Footer Apply — composes the one combined navigation from every pending
    // selection's own [param, value] pair plus price (param names read
    // straight off the number inputs' own `name` attributes) plus the
    // current sort. This is the only place a batched interaction ever
    // navigates.
    //
    // Two sources for a pair, neither ever hand-built: every group except
    // metal captures its pair by diffing its own real Shopify href against
    // the current page (paramPairFromDiff, above); metal has no per-value
    // DOM link left to diff against (Change 1's coordinator fix removed the
    // hidden raw-link wrapper), so it looks its pending indices' GIDs up in
    // metalGidByIndex instead, sourced from blob.metalGids — itself built
    // server-side straight off saea_metal_filter.values, so still never a
    // hand-typed GID.
    //
    // Pending pairs sharing the same param name (every pending underlying
    // metal value under one consolidated swatch does) are appended as
    // REPEATED KEYS — filter.v.t.shopify.jewelry-material=<gid>&filter.v.t.
    // shopify.jewelry-material=<gid>&... — NOT joined into one comma-
    // separated value. Verified live against this store's actual filter
    // engine (2026-08-28): a comma-joined value silently collapses to just
    // the FIRST GID (confirmed on both the metal and color taxonomy
    // filters), while repeated keys correctly OR them — this is also
    // exactly the format Shopify's OWN url_to_add generates when a second
    // value is added to an already-active taxonomy filter (verified by
    // inspecting it directly), so this isn't a workaround, it's matching
    // the platform's real behavior over its (in this respect inaccurate)
    // documentation. Deduped per param+value so a value pending via more
    // than one path never double-appears. A no-op for every group that only
    // ever has one pending value at a time (one entry either way).
    if (showBtn) {
      showBtn.addEventListener('click', () => {
        if (showBtn.disabled) return;
        const seen = new Set();
        const params = new URLSearchParams();
        const addPair = (p, v) => {
          if (p === undefined || v === undefined) return;
          const key = `${p} ${v}`;
          if (seen.has(key)) return;
          seen.add(key);
          params.append(p, v);
        };
        optionEls.forEach((el) => {
          const group = el.dataset.group;
          const idxList = idxListByEl.get(el);
          const isPending = group === 'availability'
            ? pending.availability.has(Number(el.dataset.saeaAvailTarget))
            : !!(idxList && idxList.some((i) => pending[group].has(i)));
          if (!isPending) return;
          if (group === 'metal') {
            idxList.forEach((i) => {
              if (!pending.metal.has(i)) return;
              const gid = metalGidByIndex[i];
              if (gid !== undefined) addPair(blob.metalParam, gid);
            });
            return;
          }
          addPair(el.dataset.saeaParam, el.dataset.saeaParamValue);
        });
        if (pending.priceMin !== null && minInput) params.append(minInput.name, String(pending.priceMin / 100));
        if (pending.priceMax !== null && maxInput) params.append(maxInput.name, String(pending.priceMax / 100));
        const sortSel = this.querySelector('#SaeaSortBy');
        if (sortSel && sortSel.value) params.append('sort_by', sortSel.value);
        const base = blob.baseUrl || location.pathname;
        const qs = params.toString();
        location.href = qs ? `${base}?${qs}` : base;
      });
    }

    // First run, zero pending changes made yet — must reproduce
    // results.filters' own server-rendered counts exactly (see the
    // VERIFY comparison in the batch-apply report).
    recompute();
  }

  // aria-modal alone doesn't stop a screen reader's virtual cursor — or a
  // sighted keyboard user tabbing past what the scrim visually hides — from
  // reaching content behind the tray. Walk up from this element to <body>,
  // marking every SIBLING along the way inert (aria-hidden as a fallback for
  // browsers without inert support). That covers everything OUTSIDE
  // <saea-facets-tray>, but .saea-sort (sort select), .saea-count (piece
  // count), and .saea-selected (applied-filter chips / invitation copy) are
  // CHILDREN of this element that render BEFORE #SaeaTray in DOM order — the
  // sibling walk never reaches them, so they were reachable by Tab underneath
  // the scrim. Marked individually below, NOT via their shared parent
  // .saea-controls — inert cascades to descendants, and .saea-refine-btn is
  // also a child of .saea-controls but must stay live (it's the control that
  // opened the tray, now raised above the scrim; see the z-index comment on
  // .saea-controls in saea-facets-tray.css), so the parent itself can never
  // be marked inert without taking the button down with it. #SaeaTray and
  // #SaeaTrayScrim are deliberately never marked, by construction (the walk
  // only touches siblings of the element that CONTAINS them, and the
  // explicit list below names only the other four). Mirrored by
  // showBackground() below via the same hiddenEls array.
  //
  // Defensive against being called while background is already hidden: if
  // hiddenEls is already populated, do NOT re-walk. A re-walk would find every
  // sibling already inert, skip all of them (see the skip check below), and
  // overwrite hiddenEls with an empty array — losing the only record of what
  // needs restoring, so showBackground() would then restore nothing and the
  // page would be permanently inert once closed. open() already guards against
  // calling this twice without an intervening close(), but this guard holds the
  // invariant even if some future caller reaches hideBackground() another way.
  hideBackground() {
    if (this.hiddenEls && this.hiddenEls.length) return;
    this.hiddenEls = [];
    const supportsInert = 'inert' in HTMLElement.prototype;
    let node = this;
    while (node && node.parentElement) {
      const parent = node.parentElement;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === node) return;
        if (sibling.hasAttribute('inert') || sibling.getAttribute('aria-hidden') === 'true') return;
        if (supportsInert) sibling.inert = true;
        else sibling.setAttribute('aria-hidden', 'true');
        this.hiddenEls.push(sibling);
      });
      node = parent;
    }

    // .saea-sort, .saea-count and .saea-selected — see the comment above
    // this method. Explicitly NOT .saea-controls itself (that would take
    // .saea-refine-btn down with it) and NOT #SaeaTray or #SaeaTrayScrim,
    // which must stay interactive while open. Pushed into the same
    // hiddenEls array so showBackground() restores them through its
    // existing generic loop, with no changes there.
    [
      this.querySelector('.saea-sort'),
      this.querySelector('.saea-count'),
      this.querySelector('.saea-selected'),
    ].forEach((el) => {
      if (!el) return;
      if (el.hasAttribute('inert') || el.getAttribute('aria-hidden') === 'true') return;
      if (supportsInert) el.inert = true;
      else el.setAttribute('aria-hidden', 'true');
      this.hiddenEls.push(el);
    });
  }

  showBackground() {
    const supportsInert = 'inert' in HTMLElement.prototype;
    (this.hiddenEls || []).forEach((el) => {
      if (supportsInert) el.inert = false;
      el.removeAttribute('aria-hidden');
    });
    this.hiddenEls = [];
  }

  open({ focus = true } = {}) {
    // Idempotent: a second open() with no intervening close() must not re-run
    // hideBackground() (see its comment for why that would strand the page
    // permanently inert). Defensive against a double-click/double-fire on
    // the Refine button — the toggle listener in connectedCallback already
    // checks is-open before calling open(), so this guard should rarely
    // trigger, but open() must stay safe to call twice in a row regardless
    // of caller.
    if (this.tray && this.tray.classList.contains('is-open')) return;
    this.lastFocused = document.activeElement;
    if (this.tray) this.tray.classList.add('is-open');
    if (this.scrim) this.scrim.classList.add('is-open');
    this.querySelectorAll('[data-saea-tray-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    this.hideBackground();
    document.addEventListener('keydown', this.onKey);
    if (focus) (this.focusables()[0] || this.tray).focus();
  }

  close() {
    // Reset first: if this.scrim were ever null the classList call below would
    // throw, and with overflow reset last that would leave the page permanently
    // unscrollable. Guarding this.scrim (matching this.tray's guard) removes the
    // throw risk too, but resetting overflow first means even an unrelated throw
    // later in this method can't strand the page. Safe to call when already
    // closed — classList.remove/showBackground/removeEventListener are all
    // no-ops on already-clean state, and lastFocused is cleared after use so a
    // second close() doesn't re-focus anything.
    document.body.style.overflow = '';
    if (this.tray) this.tray.classList.remove('is-open');
    if (this.scrim) this.scrim.classList.remove('is-open');
    this.querySelectorAll('[data-saea-tray-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.showBackground();
    document.removeEventListener('keydown', this.onKey);
    if (this.lastFocused) {
      this.lastFocused.focus();
      this.lastFocused = null;
    }
  }
}
// Resilience fallback: if define() itself throws (e.g. a third-party script
// already broke customElements, or redefinition), the tray would otherwise
// never upgrade and the Refine button would be silently dead. Fall back to
// the static flattened panel (see the `html.saea-tray-fallback` CSS block in
// saea-facets-tray.css) instead of leaving filtering unreachable. This is the
// only change to this line — no other tray logic is touched.
try {
  customElements.define('saea-facets-tray', SaeaFacetsTray);
} catch (err) {
  document.documentElement.classList.add('saea-tray-fallback');
}
